<script setup lang="ts">
import { computed, ref, watch, onBeforeUnmount } from 'vue';
import { classLabel, xpProgress, type HeroProfile, type EffectiveStats } from '../game/hero-api';
import type { Inventory, Artifact, EquipSlot } from '../game/inventory-api';
import { classArt, classIdle, itemThumb, UI, BG } from '../game/art';
import studyLive from '../assets/fimbulwake/backgrounds/study-live.mp4';
import { STAT_META, SLOT_LABELS } from '../game/item-math';
import { useRegenClock, fmtEta } from '../game/regen-clock';
import { toast } from '../game/toast';
import QuickRecover from './QuickRecover.vue';
import { t } from '../i18n';

/** Tap/enter on a stat chip explains it — hover users get the same line as a
 *  native tooltip (QA punch-list 07.08: "what does each stat do?"). */
function explainStat(meta: { label: string; desc: string }) {
  toast(`${meta.label} — ${meta.desc}`);
}

// The room's ambient loop is the ONLY thing this suppresses, and the OS switch
// is the first suspect whenever "the hero screen isn't animated" comes back from
// QA — so it is read reactively, not once at mount.
const motionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
const reducedMotion = ref(motionQuery?.matches ?? false);
const onMotionPref = (e: MediaQueryListEvent) => { reducedMotion.value = e.matches; };
motionQuery?.addEventListener?.('change', onMotionPref);
onBeforeUnmount(() => motionQuery?.removeEventListener?.('change', onMotionPref));

const props = defineProps<{ hero: HeroProfile; stats: EffectiveStats | null; inventory: Inventory | null }>();

// ── The figure LIVES (CU-86cbc65ny item 2). An alpha-keyed idle loop of the
//    class portrait plays over the still. The still stays mounted beneath it as
//    poster/fallback — autoplay can be refused (iOS Low Power Mode), the video
//    can fail to decode, and reduced-motion drops the layer entirely; in every
//    one of those cases the screen must hold the exact art it holds today.
//    `figureLive` flips only on the video's own `playing` event, so the still
//    (and its CSS sway) never lets go until real frames are actually moving.
const idleSrc = computed(() => classIdle(props.hero.hero.class));
const figureLive = ref(false);
watch(idleSrc, () => { figureLive.value = false; });
const emit = defineEmits<{
  (e: 'inspect', artifact: Artifact, slot: EquipSlot): void;
  (e: 'filter-request', slot: EquipSlot): void;
  (e: 'updated'): void;
}>();

/**
 * THE KIT — the character screen every RPG player already knows how to read:
 * the name over the crown, the figure dead centre on his ring, three gear
 * sockets down each flank, and the vitals in one band along the foot. It is
 * built to hold ONE viewport — the profile view locks scrolling on desktop
 * (see .profile-main in HeroScreen), so every band here is flex/grid-sized
 * against the panel, never against content that could push past the fold.
 */
/** Two rails of three, evenly spaced by flexbox — never by percentages. The old
 *  top:2%/37%/72% offsets drifted with panel height, which is exactly what read
 *  as crooked. A space-around column cannot drift. */
const RAIL_L: EquipSlot[] = ['helmet', 'armor', 'weapon'];
const RAIL_R: EquipSlot[] = ['amulet', 'ring', 'boots'];


const hpMax = computed(() => props.stats?.effective.maxHp ?? props.hero.hero.stats.maxHp ?? 1);
const hpPct = computed(() => Math.max(0, Math.round((props.hero.hero.stats.hp / hpMax.value) * 100)));
const nervePct = computed(() =>
  Math.max(0, Math.round((props.hero.hero.nerve.current / Math.max(1, props.hero.hero.nerve.max)) * 100)));
const xp = computed(() => xpProgress(props.hero.progression.xp));
const xpPct = computed(() => Math.round((xp.value.current / xp.value.needed) * 100));

/** Health colours the boards: aurora hale, ember hurt, blood critical. Below 30%
 *  the surviving boards splinter (`is-splintered`, see .bar-shield in app.css). */
const hpTone = computed(() =>
  hpPct.value > 50 ? 'var(--color-aurora)' : hpPct.value > 25 ? 'var(--color-ember)' : 'var(--color-error)');

// Regen countdowns (client-ticked from the profile's fractional anchors).
const hpClock = useRegenClock(() =>
  ({ anchor: props.hero.hero.regen?.hp, max: hpMax.value }));
const nerveClock = useRegenClock(() =>
  ({ anchor: props.hero.hero.regen?.nerve, max: props.hero.hero.nerve.max }));

const statRows = computed(() => {
  const eff = props.stats?.effective; const gear = props.stats?.gear; const h = props.hero;
  const rows = (['attack', 'defense', 'magic', 'speed'] as const).map((k) => ({
    key: k,
    meta: STAT_META[k],
    val: eff?.[k] ?? h.hero.stats[k],
    gear: k === 'speed' ? (gear?.speed ?? 0) : (gear?.[k] ?? 0),
  }));
  const max = Math.max(...rows.map((r) => r.val), 1);
  return rows.map((r) => ({ ...r, pct: Math.max(6, Math.round((r.val / max) * 100)) }));
});

/** Active boosts — only the ones currently live. */
const activeBoosts = computed(() => {
  const b = props.stats?.boosts;
  if (!b) return [];
  const out: { label: string; color: string }[] = [];
  if (b.cashRush) out.push({ label: t('heroCard.silverRush'), color: 'var(--color-gold)' });
  if (b.xpBooster) out.push({ label: t('heroCard.xpBoost'), color: 'var(--color-aurora-cool)' });
  if (b.revive) out.push({ label: t('heroCard.reviveReady'), color: 'var(--color-aurora)' });
  return out;
});

function equippedIn(slot: EquipSlot): Artifact | null {
  return props.inventory?.equipped[slot] ?? null;
}
function onSlotClick(slot: EquipSlot) {
  const a = equippedIn(slot);
  if (a) emit('inspect', a, slot);
  else emit('filter-request', slot);
}
</script>

<template>
  <!-- ══ THE KIT ══ one viewport, one scene. Crown (name) · arena (rails |
       figure | rails) · foot (gauges, stats, recovery). The hall art fills the
       whole panel; the haze darkens the crown and the foot so type always sits
       on ground, and leaves the middle bright so the figure sits in LIGHT. -->
  <div class="kit bound-frame relative overflow-hidden border border-border">
    <div class="kit-stage">
      <img class="kit-scene" :src="BG.study" alt="" aria-hidden="true" />
      <!-- The hall BREATHES: a generated ambient loop of this very scene, laid
           over the still (which stays as poster/fallback, so nothing flashes
           empty while it buffers or when motion is suppressed). The FIGURE is a
           separate layer above this and is untouched — only the room moves. -->
      <video
        v-if="!reducedMotion"
        class="kit-scene kit-scene-live"
        :src="studyLive" :poster="BG.study"
        autoplay muted loop playsinline disablepictureinpicture
        aria-hidden="true" tabindex="-1"
      ></video>
      <span class="kit-haze" aria-hidden="true"></span>

      <!-- the crown -->
      <header class="kit-crown">
        <div class="kit-class">{{ classLabel(hero.hero.class) }}</div>
        <div class="kit-titlerow">
          <h1 class="kit-name gilt-text">{{ hero.hero.name }}</h1>
          <span class="kit-lv"><i>{{ t('heroCard.lvAbbr') }}</i>{{ hero.progression.level }}</span>
        </div>
        <span class="kit-rule" aria-hidden="true"></span>
      </header>

      <!-- the arena -->
      <div class="kit-arena">
        <div class="kit-rail kit-rail--l">
          <button
            v-for="slot in RAIL_L" :key="slot"
            class="kit-slot"
            :class="{ 'is-filled': equippedIn(slot) }"
            :title="equippedIn(slot)?.name ?? t('heroCard.slotEmpty', { slot: SLOT_LABELS[slot] })"
            @click="onSlotClick(slot)"
          >
            <img :src="itemThumb(slot.toUpperCase(), equippedIn(slot)?.rarity, equippedIn(slot)?.name)" :alt="SLOT_LABELS[slot]" />
            <span class="kit-slot-tag">{{ SLOT_LABELS[slot] }}</span>
          </button>
        </div>

        <div class="kit-fwrap">
          <div class="kit-ring" :style="{ backgroundImage: `url(${UI.runeRing})` }" aria-hidden="true"></div>
          <!-- The still keeps the CSS sway only until the loop takes over —
               otherwise it would drift out from under the video's edges. -->
          <img
            v-if="classArt(hero.hero.class)"
            class="kit-figure"
            :class="{ 'hc-breathe': !figureLive, 'is-understudy': figureLive }"
            :src="classArt(hero.hero.class)" :alt="hero.hero.class"
          />
          <video
            v-if="idleSrc && !reducedMotion"
            class="kit-figure kit-figure-live"
            :class="{ 'is-live': figureLive }"
            :src="idleSrc"
            autoplay muted loop playsinline disablepictureinpicture
            aria-hidden="true" tabindex="-1"
            @playing="figureLive = true"
            @error="figureLive = false"
          ></video>
        </div>

        <div class="kit-rail kit-rail--r">
          <button
            v-for="slot in RAIL_R" :key="slot"
            class="kit-slot"
            :class="{ 'is-filled': equippedIn(slot) }"
            :title="equippedIn(slot)?.name ?? t('heroCard.slotEmpty', { slot: SLOT_LABELS[slot] })"
            @click="onSlotClick(slot)"
          >
            <img :src="itemThumb(slot.toUpperCase(), equippedIn(slot)?.rarity, equippedIn(slot)?.name)" :alt="SLOT_LABELS[slot]" />
            <span class="kit-slot-tag">{{ SLOT_LABELS[slot] }}</span>
          </button>
        </div>
      </div>

      <!-- the foot -->
      <div class="kit-under">
        <div class="kit-gauges">
          <div class="kit-gauge">
            <div class="kit-gauge-head">
              <span class="kit-h">{{ t('heroCard.health') }}<i v-if="hpClock && !hpClock.full" class="kit-eta" :title="t('heroCard.healthRegen', { time: fmtEta(hpClock.fullIn) })">{{ t('heroCard.fullIn', { time: fmtEta(hpClock.fullIn) }) }}</i></span>
              <span class="kit-num">{{ hero.hero.stats.hp }} / {{ hpMax }}</span>
            </div>
            <div class="bar-shield" :class="{ 'is-splintered': hpPct <= 30 }"
                 :style="{ '--sh-pct': hpPct, '--sh-tone': hpTone }">
              <div class="bar-shield-fill"></div>
            </div>
          </div>
          <div class="kit-gauge">
            <div class="kit-gauge-head">
              <span class="kit-h">{{ t('heroCard.level', { level: hero.progression.level }) }}</span>
              <span class="kit-num kit-num--dim">{{ xp.current }} / {{ xp.needed }}</span>
            </div>
            <div class="bar-shield" :style="{ '--sh-pct': xpPct, '--sh-tone': 'var(--color-aurora-cool)', '--sh-boards': 12 }">
              <div class="bar-shield-fill"></div>
            </div>
          </div>
          <div class="kit-gauge">
            <div class="kit-gauge-head">
              <span class="kit-h">{{ t('heroCard.fury') }}<i v-if="nerveClock && !nerveClock.full" class="kit-eta" :title="t('heroCard.furyRegen', { time: fmtEta(nerveClock.nextIn) })">{{ t('heroCard.nextIn', { time: fmtEta(nerveClock.nextIn) }) }}</i></span>
              <span class="kit-num">{{ hero.hero.nerve.current }} / {{ hero.hero.nerve.max }}</span>
            </div>
            <div class="bar-shield" :style="{ '--sh-pct': nervePct, '--sh-tone': 'var(--color-gold)', '--sh-boards': Math.min(hero.hero.nerve.max, 14) }">
              <div class="bar-shield-fill"></div>
            </div>
          </div>
        </div>

        <div class="kit-tally">
          <div class="kit-stats">
            <!-- Tooltip on hover, toast on tap — the same line serves both,
                 because half the QA screenshots come from touch devices. -->
            <div
              v-for="r in statRows" :key="r.key" class="kit-stat" :style="{ '--c': r.meta.color }"
              :title="r.meta.label + ' — ' + r.meta.desc"
              role="button" tabindex="0"
              @click="explainStat(r.meta)" @keydown.enter="explainStat(r.meta)"
            >
              <span class="kit-stat-k">{{ r.meta.short }}</span>
              <span class="kit-stat-v">{{ r.val }}<i v-if="r.gear">+{{ r.gear }}</i></span>
              <span class="kit-stat-bar"><b :style="{ width: r.pct + '%' }"></b></span>
            </div>
          </div>
          <div v-if="activeBoosts.length" class="kit-boosts">
            <span v-for="bst in activeBoosts" :key="bst.label" class="kit-boost" :style="{ '--c': bst.color }">{{ bst.label }}</span>
          </div>
        </div>

        <QuickRecover
          :hp="hero.hero.stats.hp" :max-hp="hpMax" :full-hp="stats?.base.maxHp"
          :hp-regen="hero.hero.regen?.hp" :nerve-regen="hero.hero.regen?.nerve"
          :nerve="hero.hero.nerve.current" :max-nerve="hero.hero.nerve.max"
          :cash="hero.progression.cash" :gold-bars="hero.progression.goldBars"
          @updated="emit('updated')"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.kit {
  border-radius: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--surface-plank);
  background-size: var(--surface-plank-size);
  background-blend-mode: var(--surface-plank-blend);
}

/* ── THE STAGE — fills the panel; every band inside sizes against IT ───────── */
.kit-stage {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 24px 22px 14px; /* 24 top: bound-frame's iron strap */
}
.kit-scene {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover; object-position: center 32%;
  filter: saturate(1.06);
}
/* The ambient loop shares the still's box exactly, so it registers as the same
   painting rather than a second one. Cheap to composite: no filters of its own
   beyond the still's saturation, and it never takes pointer events. */
.kit-scene-live { pointer-events: none; }
/* symmetric haze: ground under the crown, LIGHT in the middle for the figure,
   heavy ground under the foot band; aurora breathes at the crown */
.kit-haze {
  position: absolute; inset: 0;
  background:
    radial-gradient(50% 30% at 50% 0%, rgba(111, 216, 192, 0.12), transparent 72%),
    radial-gradient(150% 110% at 50% 42%, transparent 48%, rgba(6, 10, 18, 0.62) 100%),
    linear-gradient(180deg,
      rgba(7, 12, 20, 0.72) 0%, rgba(7, 12, 20, 0.28) 17%,
      rgba(7, 12, 20, 0.14) 42%, rgba(8, 13, 22, 0.60) 66%,
      rgba(8, 13, 22, 0.93) 84%, rgba(8, 13, 22, 0.97) 100%);
  animation: kit-fire 4.6s ease-in-out infinite;
}
@keyframes kit-fire {
  0%, 100% { opacity: 1; }
  37% { opacity: 0.94; }
  58% { opacity: 0.985; }
  81% { opacity: 0.955; }
}

/* ── the crown ─────────────────────────────────────────────────────────────── */
.kit-crown {
  position: relative; z-index: 2;
  flex: none;
  text-align: center;
}
.kit-class {
  font-family: var(--font-label, sans-serif);
  text-transform: uppercase; letter-spacing: 0.09em;
  font-size: 11.5px; color: color-mix(in srgb, var(--color-aurora) 68%, white 22%);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.9);
}
.kit-titlerow {
  display: flex; align-items: baseline; justify-content: center; gap: 12px;
  min-width: 0;
}
.kit-name {
  font-family: var(--font-heading, serif);
  font-size: clamp(24px, 2.6vw, 34px); line-height: 1.05;
  /* fill and glow come from .gilt-text */
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  min-width: 0;
}
.kit-rule {
  display: block; width: 210px; max-width: 60%; height: 9px; margin: 6px auto 0;
  background: var(--orn-divider-gold) no-repeat center / contain;
}
/* the level, struck into an iron tag rather than set in a gold pill */
.kit-lv {
  flex: none;
  display: inline-flex; align-items: baseline; gap: 4px;
  padding: 5px 10px;
  clip-path: var(--clip-hewn);
  background: var(--mat-iron);
  font-family: var(--font-heading, serif);
  font-size: 17px; color: #e7eef7;
  text-shadow: 0 -1px 0 rgba(0, 0, 0, 0.85);
}
.kit-lv i {
  font-family: var(--font-label, sans-serif); font-style: normal;
  font-size: 10px; letter-spacing: 0.07em; text-transform: uppercase;
  color: #c6d4e4;
}

/* ── the arena: rail | figure | rail ───────────────────────────────────────── */
.kit-arena {
  --floor: 16px;
  /* Socket and rail spacing SCALE with viewport height. They used to be fixed
     (54px sockets, 30px gaps), while .kit-arena is `flex: 1 1 auto; min-height:
     0` inside a locked one-viewport panel — so on a short laptop window the
     arena shrank below the rails' natural height and the rails, being
     `justify-content: center` with rigid children, spilled out of it in both
     directions: the lower sockets slid under the vitals row and their tags
     collided with it, and the last one was cut off entirely. Scaling means a
     short screen makes the kit smaller instead of making it overlap. */
  --kit-slot: clamp(38px, 5.6vh, 54px);
  --kit-rail-gap: clamp(12px, 2.4vh, 30px);
  position: relative; z-index: 2;
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: stretch;
  gap: 8px;
  padding-top: 6px;
}
.kit-rail {
  display: flex; flex-direction: column;
  justify-content: center;
  gap: var(--kit-rail-gap); /* room for each socket's tag below it */
  padding-bottom: clamp(6px, 1.2vh, 14px);
}
.kit-fwrap {
  position: relative;
  min-height: 0;
}
.kit-figure {
  position: absolute; inset: 0 0 var(--floor);
  width: 100%;
  /* whichever is smaller: the art's native ~600px render, or the room the
     viewport actually gives — a short screen shrinks him, never scrolls */
  max-height: min(560px, 100%);
  margin: auto auto 0;
  object-fit: contain; object-position: 50% 100%;
  filter: drop-shadow(0 10px 24px rgba(0, 0, 0, 0.7));
}
.kit-ring {
  position: absolute;
  left: 50%; bottom: var(--floor);
  width: min(72%, 340px); aspect-ratio: 1;
  transform: translate(-50%, 50%) rotateX(76deg);
  background-size: contain; background-position: center; background-repeat: no-repeat;
  mix-blend-mode: screen;
  /* screen-blending is inert here: .kit-arena carries position + z-index, so
     the blend context stops there and never reaches the hall scene — the
     art's opaque black square rendered as-is (QA: "black corners under the
     hero"). The mask clips the square to the disc, which is what the blend
     was supposed to achieve for the corners. */
  -webkit-mask-image: radial-gradient(closest-side, #000 94%, transparent 100%);
  mask-image: radial-gradient(closest-side, #000 94%, transparent 100%);
  opacity: 0.9;
  animation: kit-turn 64s linear infinite;
}
@keyframes kit-turn {
  from { transform: translate(-50%, 50%) rotateX(76deg) rotate(0deg); }
  to   { transform: translate(-50%, 50%) rotateX(76deg) rotate(360deg); }
}

/* a slot is a socket cut into the wall, with a struck boss when it's empty */
.kit-slot {
  position: relative;
  width: var(--kit-slot); height: var(--kit-slot);
  padding: 3px;
  background: none;
  transition: transform 120ms ease;
}
/* THE SOCKET. Its shape lives here, not on the button, because clip-path clips a
   box's DESCENDANTS — and .kit-slot-tag hangs BELOW the button, so clipping the
   button erased every socket label. */
.kit-slot::before {
  content: '';
  position: absolute; inset: 0; z-index: 0;
  clip-path: var(--clip-hewn);
  background:
    var(--orn-boss-dark) no-repeat center,
    var(--orn-boss-lite) no-repeat top calc(50% + 1px) center,
    linear-gradient(180deg, #0b0f14, #141a22 60%, #0a0e13);
  box-shadow:
    inset 0 2px 4px rgba(0, 0, 0, 0.85),
    inset 0 -1px 0 rgba(226, 236, 248, 0.07);
  transition: box-shadow 160ms ease;
}
/* an engraved tie from each socket toward the figure it dresses */
.kit-slot::after {
  content: '';
  position: absolute; top: 50%; width: 22px; height: 2px;
  background: linear-gradient(90deg, rgba(207, 232, 255, 0.30), transparent);
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.55);
  pointer-events: none;
}
.kit-rail--l .kit-slot::after { left: 100%; }
.kit-rail--r .kit-slot::after {
  right: 100%;
  background: linear-gradient(270deg, rgba(207, 232, 255, 0.30), transparent);
}
.kit-slot img {
  position: relative; z-index: 1;
  width: 100%; height: 100%; object-fit: cover;
  opacity: 0.18; transition: opacity 160ms ease;
}
/* filled: the item covers the boss, so drop it — a boss behind an icon is noise */
.kit-slot.is-filled::before { background-image: linear-gradient(180deg, #0b0f14, #141a22 60%, #0a0e13); }
.kit-slot.is-filled img { opacity: 1; }
.kit-slot:hover { transform: translateY(-1px); }
.kit-slot:hover::before {
  box-shadow:
    inset 0 2px 4px rgba(0, 0, 0, 0.85),
    inset 0 0 14px color-mix(in srgb, var(--color-aurora) 30%, transparent),
    inset 0 -1px 0 color-mix(in srgb, var(--color-aurora) 60%, transparent);
}
/* the slot's name, carved under it — a paperdoll needs its sockets labelled or
   an empty one is just a dark square */
.kit-slot-tag {
  position: absolute; top: calc(100% + 3px); left: 50%;
  transform: translateX(-50%);
  z-index: 1;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.95), 0 0 6px rgba(0, 0, 0, 0.8);
  font-family: var(--font-label, sans-serif);
  font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase;
  color: #dce7f3;
  white-space: nowrap;
  /* same reasoning as the foot plate: a socket tag lands wherever the painting
     happens to be bright, so it carries its own small ground */
  padding: 2px 7px;
  background: rgba(5, 9, 16, 0.78);
  clip-path: var(--clip-hewn);
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--color-aurora) 16%, transparent);
}
.kit-slot:hover .kit-slot-tag { color: var(--color-aurora); }

/* ── the foot: gauges, tallies, boosts ─────────────────────────────────────
   Owner 02.09: "нихрена не видно" — the whole foot was set in 8-10px greys
   over a lit tavern painting, which is the worst ground a small dim label can
   sit on. Every label, value and slot tag below is a step larger and pulled
   toward white; the bars gained a pixel. Nothing here is decoration — it is
   the numbers the player builds around.

   Bigger type alone did NOT fix it (owner, second pass: "still lost against
   the background") — candles and bone-white clutter still burned through the
   haze behind the readings, and a text shadow cannot save a label whose
   ground changes brightness under every glyph. So the foot stops floating on
   the painting and gets its OWN plate: a near-opaque slab bled to the panel's
   edges, blurred so the scene reads as depth behind it rather than texture
   through it. The haze still does the soft work above; this does the hard
   work under the numbers. */
.kit-under {
  position: relative; z-index: 2;
  flex: none;
  display: flex; flex-direction: column; gap: 10px;
  /* out to the stage's own padding on three sides, so the plate meets the frame */
  margin: 0 -22px -14px;
  padding: 12px 22px 14px;
  background: linear-gradient(180deg,
    rgba(6, 10, 17, 0.62) 0%, rgba(6, 10, 17, 0.9) 18%, rgba(4, 8, 14, 0.95) 100%);
  backdrop-filter: blur(4px);
  box-shadow: inset 0 1px 0 color-mix(in srgb, var(--color-aurora) 22%, transparent);
}
.kit-gauges {
  display: grid; grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px 16px;
}
.kit-gauge { min-width: 0; }
.kit-gauge-head { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 4px; }
.kit-h {
  font-family: var(--font-label, sans-serif);
  text-transform: uppercase; letter-spacing: 0.08em;
  font-size: 12px; color: #cfdcec;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.85);
  white-space: nowrap;
}
.kit-eta {
  font-family: var(--font-body, serif); font-style: normal;
  text-transform: none; letter-spacing: 0.02em;
  font-size: 11.5px; font-variant-numeric: tabular-nums;
  color: #adbccd;
  margin-left: 7px;
}
.kit-num { font-family: var(--font-body, serif); font-size: 14.5px; font-variant-numeric: tabular-nums; color: var(--color-text); }
.kit-num--dim { color: #c3d1e1; }

/* combat stats: a struck number with a short cut bar under it. Four more
   full-width meters would have made the panel a bar chart. */
.kit-tally {
  display: flex; align-items: flex-end; justify-content: space-between;
  gap: 10px 18px; flex-wrap: wrap;
}
.kit-stats {
  flex: 1 1 auto; min-width: 0;
  display: grid; grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px 14px;
}
.kit-stat { min-width: 0; }
/* the stat tiles are tappable (they explain themselves in a toast), so on touch
   they owe the same 44px floor as any other control — they measured 37px */
@media (pointer: coarse) {
  .kit-stat { min-height: 44px; display: flex; flex-direction: column; justify-content: center; }
}
.kit-stat-k {
  display: block;
  font-family: var(--font-label, sans-serif);
  font-size: 11px; letter-spacing: 0.09em; text-transform: uppercase;
  color: #cfdcec;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
}
.kit-stat-v {
  display: block;
  font-family: var(--font-heading, serif); font-size: 21px; line-height: 1.1;
  color: #eef4fb;
  text-shadow: 0 -1px 0 rgba(0, 0, 0, 0.8);
}
.kit-stat-v i { font-style: normal; font-size: 12px; color: var(--color-aurora); margin-left: 3px; }
.kit-stat-bar {
  display: block; height: 4px; margin-top: 4px;
  background: rgba(0, 0, 0, 0.6);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.7);
}
.kit-stat-bar b {
  display: block; height: 100%;
  background: var(--c);
  box-shadow: 0 0 7px color-mix(in srgb, var(--c) 60%, transparent);
  transition: width 700ms ease;
}

.kit-boosts { display: flex; flex-wrap: wrap; gap: 6px; justify-content: flex-end; }
.kit-boost {
  font-family: var(--font-label, sans-serif);
  text-transform: uppercase; letter-spacing: 0.07em;
  font-size: 10.5px; padding: 4px 10px;
  clip-path: var(--clip-hewn);
  color: var(--c);
  background: color-mix(in srgb, var(--c) 13%, rgba(0, 0, 0, 0.45));
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--c) 32%, transparent);
}

/* the figure SWAYS — two axes on co-prime periods, so it reads as a man shifting
   his weight rather than a sprite scaling. Overrides the shared zoom-pulse. */
.kit-figure.hc-breathe { animation: kit-sway 7.3s ease-in-out infinite; }
@keyframes kit-sway {
  0%, 100% { transform: translateX(0) scale(1); }
  32% { transform: translateX(1.5px) scale(1.008); }
  66% { transform: translateX(-1.5px) scale(1.014); }
}
/* The idle loop rides the exact same box as the still (same .kit-figure sizing
   rules — object-fit works on <video> too) and only becomes visible once its
   frames are moving; until then the still holds the stage alone. The still then
   drops away entirely (`is-understudy`) — its edges would ghost out from under
   the moving figure otherwise. */
.kit-figure-live { opacity: 0; pointer-events: none; transition: opacity 480ms ease; }
.kit-figure-live.is-live { opacity: 1; }
.kit-figure.is-understudy { opacity: 0; transition: opacity 480ms ease; }
@media (prefers-reduced-motion: reduce) {
  /* ambience, not information — all of it stops */
  .kit-ring, .kit-haze, .kit-figure.hc-breathe { animation: none; }
}

/* Below the viewport lock the page scrolls; the arena takes a fixed stature
   instead of a share of a height that no longer exists.

   This is the EXACT COMPLEMENT of HeroScreen.vue's
   `(min-width: 1101px) and (min-height: 800px)` — 1100/1101 and 799/800 — and
   `hero-viewport-lock.test.ts` fails if the two drift apart. The height floor
   was 599px and that was too low: from 600 to 800 the lock engaged but the
   arena could not hold its own socket rails, so the tags landed on the vitals
   (QA 86cb9yd3p item 3; the measurements are in HeroScreen.vue beside the
   matching query). */
@media (max-width: 1100px), (max-height: 799px) {
  .kit { height: auto; }
  .kit-arena { min-height: 340px; }
}
@media (max-width: 640px) {
  .kit-arena { gap: 4px; --kit-slot: 46px; }
  .kit-slot::after { width: 10px; }
  .kit-gauges { grid-template-columns: minmax(0, 1fr); }
  .kit-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
</style>
